import {
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type KeyboardEvent,
  type ReactNode
} from "react";
import { Link } from "react-router-dom";
import { BrandLogo, type BrandingArtwork } from "./brand";
import { AccountMenu } from "./account-menu";
import { NavigationIcon, type NavigationIconName } from "./navigation-icon";
import { formatOrganizationDisplayName } from '@pustak-lms/utils';

export interface NavigationItem {
  label: string;
  href: string;
  marker?: string;
  icon?: NavigationIconName;
  children?: NavigationItem[];
}

function SidebarNavigationItem({ item, activeHref, collapsed, onNavigate, onExpand }: {
  item: NavigationItem;
  activeHref: string;
  collapsed: boolean;
  onNavigate: () => void;
  onExpand: () => void;
}) {
  const submenuId = useId();
  const containsActive = (entry: NavigationItem): boolean => entry.href === activeHref || Boolean(entry.children?.some(containsActive));
  const active = containsActive(item);
  const [expanded, setExpanded] = useState(active);
  useEffect(() => { if (active) setExpanded(true); }, [activeHref, active]);
  const content = <><NavigationIcon name={item.icon ?? "overview"} /><span className="nav-label">{item.label}</span>{item.marker ? <span className="nav-marker">{item.marker}</span> : null}</>;
  if (!item.children?.length) return <Link className={active ? "active" : undefined} aria-current={active ? "page" : undefined} to={item.href} title={collapsed ? item.label : undefined} onClick={onNavigate}>{content}</Link>;
  const open = expanded && !collapsed;
  return <div className="nav-group">
    <button type="button" className={`nav-group-toggle${active ? " is-active" : ""}`} title={collapsed ? item.label : undefined} aria-expanded={open} aria-controls={submenuId} onClick={() => { if (collapsed) { onExpand(); setExpanded(true); } else setExpanded(value => !value); }}>
      {content}<svg className="nav-group-chevron" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d={open ? "m5 12 5-5 5 5" : "m5 8 5 5 5-5"} /></svg>
    </button>
    <div className="nav-submenu" id={submenuId} hidden={!open}>{item.children.map(child => <SidebarNavigationItem key={child.href} item={child} activeHref={activeHref} collapsed={collapsed} onNavigate={onNavigate} onExpand={onExpand} />)}</div>
  </div>;
}

interface AppShellProps {
  appName: string;
  basePath?: string;
  homeHref?: string;
  workspaceName?: string;
  workspaceStatus?: ReactNode;
  navigation: NavigationItem[];
  activeHref: string;
  user: {
    name: string;
    role: string;
  };
  children: ReactNode;
  headerContent?: ReactNode;
  branding?: BrandingArtwork;
  showHeaderBadge?: boolean;
}

export function AppShell({
  appName,
  basePath = "",
  homeHref,
  workspaceName,
  workspaceStatus,
  navigation,
  activeHref,
  user,
  children,
  headerContent,
  branding,
  showHeaderBadge = true
}: AppShellProps) {
  const displayWorkspaceName = workspaceName ? formatOrganizationDisplayName(workspaceName) : undefined;
  const displayBrandName = branding?.name ? formatOrganizationDisplayName(branding.name) : appName;
  const sidebarId = useId();
  const sidebarRef = useRef<HTMLElement>(null);
  const mobileToggleRef = useRef<HTMLButtonElement>(null);
  const mobileCloseRef = useRef<HTMLButtonElement>(null);
  const preferenceKey = `pustak.sidebar.collapsed.${appName}`;
  const [collapsed, setCollapsed] = useState(() => {
    try { return typeof window !== "undefined" && window.localStorage.getItem(preferenceKey) === "true"; }
    catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem(preferenceKey, String(collapsed)); }
    catch { /* Navigation still works when browser storage is unavailable. */ }
  }, [collapsed, preferenceKey]);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 760px)");
    const closeOnDesktop = () => { if (!media.matches) setMobileOpen(false); };
    media.addEventListener("change", closeOnDesktop);
    return () => media.removeEventListener("change", closeOnDesktop);
  }, []);

  useEffect(() => {
    if (!mobileOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    mobileCloseRef.current?.focus();

    function handleKeyDown(event: globalThis.KeyboardEvent) {
      if (event.defaultPrevented) return;
      if (event.key === "Escape") {
        event.preventDefault();
        setMobileOpen(false);
      } else if (event.key === "Tab") {
        const items = Array.from(sidebarRef.current?.querySelectorAll<HTMLElement>('a[href], button:not(:disabled), [tabindex]:not([tabindex="-1"])') ?? [])
          .filter((item) => item.tabIndex >= 0 && item.getClientRects().length > 0);
        const first = items[0];
        const last = items.at(-1);
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      if (window.matchMedia("(max-width: 760px)").matches) mobileToggleRef.current?.focus();
    };
  }, [mobileOpen]);

  const appRoute = (href: string) => {
    if (!basePath) return href;
    if (href === basePath) return "/";
    return href.startsWith(`${basePath}/`) ? href.slice(basePath.length) : href;
  };
  return (
    <div className={["app-shell bg-background text-foreground font-body", collapsed ? "sidebar-collapsed" : "", mobileOpen ? "sidebar-mobile-open" : ""].filter(Boolean).join(" ")}>
      <aside className="sidebar" id={sidebarId} ref={sidebarRef} role={mobileOpen ? "dialog" : undefined} aria-modal={mobileOpen ? true : undefined} aria-label="Workspace sidebar">
        <div className="sidebar-brand-row">
          <Link className="brand" to={appRoute(homeHref ?? `${basePath}/`)} aria-label={`${displayBrandName} home`} onClick={() => setMobileOpen(false)}>
            <BrandLogo src={branding?.logoUrl} alt="" />
            <BrandLogo src={branding?.iconUrl} variant="mark" alt="" />
          </Link>
          <button className="sidebar-toggle" type="button" aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"} aria-expanded={!collapsed} aria-controls={sidebarId} onClick={() => setCollapsed((value) => !value)}>
            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d={collapsed ? "m6 5 5 5-5 5m4-10 5 5-5 5" : "m10 5-5 5 5 5m4-10-5 5 5 5"} /></svg>
          </button>
          <button className="mobile-sidebar-close" type="button" ref={mobileCloseRef} aria-label="Close navigation" onClick={() => setMobileOpen(false)}>
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m6 6 12 12M6 18 18 6" /></svg>
          </button>
        </div>
        <div className="sidebar-content">
          {displayWorkspaceName ? <div className="workspace-chip" title={displayWorkspaceName}>
            <span className="workspace-icon">{displayWorkspaceName.slice(0, 1)}</span>
            <span>
              <small>Workspace</small>
              <strong>{displayWorkspaceName}</strong>
              {workspaceStatus ? <span className="workspace-status">{workspaceStatus}</span> : null}
            </span>
          </div> : null}

          <nav className="navigation" aria-label="Primary navigation">
            {navigation.map(item => <SidebarNavigationItem key={item.href} item={item} activeHref={activeHref} collapsed={collapsed} onNavigate={() => setMobileOpen(false)} onExpand={() => setCollapsed(false)} />)}
          </nav>
        </div>
      </aside>
      {mobileOpen ? <button className="sidebar-backdrop" type="button" aria-label="Dismiss navigation" tabIndex={-1} onClick={() => setMobileOpen(false)} /> : null}
      <header className="app-header" aria-label="Application header" inert={mobileOpen}>
        <button className="mobile-navigation-toggle" type="button" ref={mobileToggleRef} aria-label="Open navigation" aria-expanded={mobileOpen} aria-controls={sidebarId} onClick={() => setMobileOpen(true)}>
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 6h16M4 12h16M4 18h16" /></svg>
        </button>
        {showHeaderBadge ? <span className="app-header-badge">{appName}</span> : null}
        {headerContent}
        <AccountMenu user={user} placement="header" />
      </header>
      <main className="main-content" inert={mobileOpen}>{children}</main>
    </div>
  );
}

interface PageHeaderProps {
  eyebrow?: string;
  title: string;
  titleBadge?: ReactNode;
  description: string;
  actions?: ReactNode;
}

export function PageHeader({ eyebrow, title, titleBadge, description, actions }: PageHeaderProps) {
  return (
    <header className="page-header">
      <div>
        {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
        {titleBadge ? <div className="page-header-title"><h1>{title}</h1>{titleBadge}</div> : <h1>{title}</h1>}
        <p>{description}</p>
      </div>
      {actions ? <div className="header-actions">{actions}</div> : null}
    </header>
  );
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  variant?: "primary" | "secondary" | "outline" | "gold" | "destructive" | "ghost";
}

export function Button({
  children,
  className,
  variant = "primary",
  type = "button",
  ...props
}: ButtonProps) {
  return (
    <button
      className={[`button button-${variant}`, className].filter(Boolean).join(" ")}
      type={type}
      {...props}
    >
      {children}
    </button>
  );
}

export { Select } from "./select";
export { lockDialogScroll } from "./dialog-scroll-lock";
export { ConfirmationDialog } from "./confirmation-dialog";
export type { SelectOption, SelectProps } from "./select";

interface StatCardProps {
  label: string;
  value: string;
  detail: string;
  trend?: string;
}

export function StatCard({ label, value, detail, trend }: StatCardProps) {
  return (
    <article className="stat-card">
      <div className="stat-card-top">
        <span>{label}</span>
        {trend ? <span className="trend">{trend}</span> : null}
      </div>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}

export function StatusPill({
  children,
  tone = "neutral"
}: {
  children: ReactNode;
  tone?: "success" | "warning" | "info" | "danger" | "neutral";
}) {
  return <span className={`status-pill status-${tone}`}>{children}</span>;
}

export function ProgressBar({ value }: { value: number }) {
  return (
    <div className="progress-track" aria-label={`${value}% complete`}>
      <span style={{ width: `${Math.min(100, Math.max(0, value))}%` }} />
    </div>
  );
}

export function PlaceholderPage({ title, description }: { title: string; description: string }) {
  return (
    <section className="placeholder-page">
      <span className="placeholder-icon">◇</span>
      <h2>{title}</h2>
      <p>{description}</p>
      <StatusPill tone="info">Mock scaffold</StatusPill>
    </section>
  );
}

export { AuthBoundary, canEnter, type AuthMode } from "./auth";
export { NavigationIcon, type NavigationIconName } from "./navigation-icon";
export { BrandLogo, type BrandLogoProps, type BrandingArtwork } from "./brand";
export { studentThemeToCssVariables, deriveStudentBrandTokens, normalizeBrandColor, contrastRatio, relativeLuminance, DEFAULT_PRIMARY_COLOR, DEFAULT_SECONDARY_COLOR, type StudentBrandTokens } from "./student-branding";
export { InvitationList, InvitationManager } from "./invitations";

export { ActivationPage } from "./activation";
export { PasswordPage } from "./password";
export { ProfilePage } from "./profile";
export { AppQueryProvider, appQueryClient, cachedQueryKey, fetchCachedQuery, createCachedQueryWriter, useCachedQuery, useCachedInfiniteQuery, resetQueryCache, setQueryIdentity, PAGE_STALE_TIME, REFERENCE_STALE_TIME } from "./query";
